import { skipToken } from '@reduxjs/toolkit/query'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import penningLogo from '../../../../assets/penning.png'
import { COMPANY_INVOICE_LEGAL_LINE } from '../../../../constants/company'
import {
  INVESTMENT_PACKAGE_LABELS,
  INVESTMENT_PACKAGE_OPTIONS,
} from '../../../../constants/investmentPackages'
import { useOtacGetFundDepositInvoiceDetailsQuery } from '../../../../redux/services/coreApi'
import { setOtacToken } from '../../../../redux/slices/sessionSlice'
import { formatNumber, formatUtcDate } from '../../../../utils/formatters'

const Item: React.FC<{ title: string; value: string }> = ({ title, value }) => (
  <div className="pr-2">
    <span>{title}</span>
    <span className="text-brand-light-text-default"> {value}</span>
  </div>
)

const FundDepositInvoice: React.FC = () => {
  const [depositId, setDepositId] = useState<string | undefined>(undefined)

  const { data: invoice, isLoading } =
    useOtacGetFundDepositInvoiceDetailsQuery(depositId ?? skipToken)

  const dispatch = useDispatch()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    const otacToken = searchParams.get('otacToken')
    if (otacToken) dispatch(setOtacToken({ otacToken }))

    const id = searchParams.get('id')
    if (id) setDepositId(id)
  }, [])

  if (isLoading || !invoice) {
    return <div>Loading...</div>
  }

  const { deposit, user } = invoice
  const packageId = deposit.investmentPackage
  const packageLabel = packageId
    ? INVESTMENT_PACKAGE_LABELS[packageId]
    : 'Portfolio Management'
  const packageTagline = packageId
    ? INVESTMENT_PACKAGE_OPTIONS.find(o => o.id === packageId)?.tagline
    : undefined

  const amountInEur = deposit.amount * deposit.currencyToEurRate

  const iban = deposit.toBankAccount?.bankAccountDetails.iban
  const bic = deposit.toBankAccount?.bankAccountDetails.bic
  const accountNo = deposit.toBankAccount?.bankAccountDetails.accountNumber
  const regNo = deposit.toBankAccount?.bankAccountDetails.registration

  return (
    // data-invoice-ready signals the PDF renderer (InvoicePdfService) that the
    // invoice data is on screen — this branch only renders once data is present
    <div
      data-invoice-ready="true"
      className="h-screen p-5 text-[10px] font-medium bg-white text-brand-light-text-description relative"
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="font-semi text-xs mb-1 text-brand-light-text-default">
            {user.name}
          </p>
          {user.role === 'Organization' && <p>{user.vatNumber}</p>}
          <p>{user.email}</p>
          {user.address && <p>{user.address}</p>}
        </div>
        <img
          src={penningLogo}
          alt="Penning logo"
          className="h-16 w-16 rounded-full"
        />
      </div>

      <div className="grid grid-cols-2 mt-10">
        <Item title="Date:" value={`${formatUtcDate(deposit.transferredAt ?? deposit.createdAt)}`} />
        <Item
          title={iban ? 'IBAN:' : 'Reg. No.:'}
          value={iban ?? regNo ?? ''}
        />
        <Item title="ID:" value={`${deposit.id}`} />
        <Item
          title={bic ? 'BIC:' : 'Account No.:'}
          value={bic ?? accountNo ?? ''}
        />
        <Item
          title="Order ID:"
          value={`${deposit.bankDepositUniqueId}`}
        />
      </div>

      <div className="py-4 px-5 mt-5 text-xl text-brand-light-text-default font-semi -mx-5">
        Portfolio Management Deposit Receipt
      </div>

      <table className="mt-5 w-full">
        <thead>
          <tr>
            <th className="text-left">Package</th>
            <th className="text-left">Description</th>
            <th className="text-right">Currency</th>
            <th className="text-right">Amount</th>
            <th className="text-right">Amount (EUR)</th>
          </tr>
        </thead>
        <tbody className="border-b border-black/10 border-solid">
          <tr>
            <th className="text-left">{packageLabel}</th>
            <th className="text-left">
              {packageTagline ?? 'Portfolio Management Deposit'}
            </th>
            <th className="text-right">{deposit.currency.isoCode}</th>
            <th className="text-right">
              {formatNumber(deposit.amount, deposit.currency)}
            </th>
            <th className="text-right font-semi text-brand-light-text-default">
              {formatNumber(amountInEur, {
                currencyType: 'FiatCurrency',
                isoCode: 'EUR',
              })}
            </th>
          </tr>
        </tbody>
      </table>

      <div className="ml-auto w-4/12 py-2 border-b text-[10px] border-black/10 font-semi text-brand-light-text-default border-solid px-2">
        <div className="flex justify-between">
          <p>Total deposited:</p>
          <p>
            {formatNumber(amountInEur, {
              currencyType: 'FiatCurrency',
              isoCode: 'EUR',
            })}
          </p>
        </div>
      </div>

      <div className="absolute left-0 bottom-0 right-0 text-center py-3 border-t border-solid border-black/10 text-[10px]">
        <p>{COMPANY_INVOICE_LEGAL_LINE}</p>
        <p>support@penning.io - www.penning.dk </p>
      </div>
    </div>
  )
}

export default FundDepositInvoice
